import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import {
  Select, Input, AutoCompleteSuggestionItem,
  List, ListBody, Button, MaterialIcon, Spinner } from '@pearsontechnology/aero-components';
import appConfig from 'appConfig';
import { dispatch } from 'pluginServices/store';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { pushToast } from 'containers/ToastManager/actions';
import messages from './messages';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { featureKeys } from 'services/FeatureFlagService';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import authenticationTypes from 'constants/authenticationTypes';

import {
  SectionDetails, SourceContainer, EditContainer, RightAlignContainer,
  SearchResultsContainer, ActionContainer, ActionItemContainer,
} from './styles';

const sourceList = Object.freeze({
  NONE: 'none',
  ALFRESCO: 'Alfresco',
});

const SourceSettings = ({ settings, saveSettings, index, getFeatureFlagValue, currentUser }) => {
  const isWorkspaceIesV2Enabled = getFeatureFlagValue(featureKeys.WORKSPACE_IES_V2) && currentUser?.authenticationType === authenticationTypes.IES_V2;

  const [originalSettings, setOriginalSettings] = useState(settings);

  const [title, setTitle] = useState(settings.title);
  const [sourceType, setSourceType] = useState(settings.sourceType);
  const [documentLibraryId, setDocumentLibraryId] = useState(settings.documentLibraryId);

  const [readOnly, setReadOnly] = useState(settings.sourceType !== sourceList.NONE);
  const [showButtons, setShowButtons] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showEditIcon, setShowEditIcon] = useState(settings.sourceType !== sourceList.NONE);
  const [autoCompleteData, setAutoCompleteData] = useState([]);
  const [showSpinner, setSpinnerVisible] = useState(false);
  const showTitleInput = sourceType && sourceType !== sourceList.NONE;
  const debounceFetchSitesFn = useRef(debounce((value) => { fetchAlfrescoSites(value); }, 300));

  const fetchAlfrescoSites = async (search) => {
    setSpinnerVisible(true);
    const data = await fetch(appConfig.getAlfrescoSiteSearchURL(), {
      method: 'POST',
      body: JSON.stringify({ query: { query: `TYPE:"st:site" and cm:title:"*${search}*"` }, include: ['properties'] }),
      headers: {
        'content-type': 'application/json',
        ...(!isWorkspaceIesV2Enabled && { 'X-ApiKey': appConfig.getAlfrescoAPIKey() }),
      },
      ...(isWorkspaceIesV2Enabled && { credentials: 'include' }),
    }).then((response) => response.json()).catch((error) => {
      dispatch(pushToast({ message: `${intl.formatMessage(messages.sourceSettingsSelectAlfrescoError)} - ${error}` }));
    });
    setSpinnerVisible(false);
    const newData = [];
    if (data && data.list) {
      // for every site, we need to temporarily hold the nodeValue.
      // If the user selects this site, we would then use that nodeValue to get the documentLibraryId
      data.list.entries.forEach((entry, i) => {
        newData[i] = {
          title: entry.entry?.properties['cm:title'],
          documentLibraryId: null,
          node: entry.entry?.id,
        };
      });
    }
    setAutoCompleteData(newData);
  };

  const fetchDocumentLibraryID = async (siteId, isWorkspaceIesV2Enabled) => {
    setSpinnerVisible(true);
    const data = await fetch(`${appConfig.getAlfrescoDocumentIdURL()}/${siteId}/children?`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(!isWorkspaceIesV2Enabled && { 'X-ApiKey': appConfig.getAlfrescoAPIKey() }),
      },
      ...(isWorkspaceIesV2Enabled && { credentials: 'include' }),
    }).then((response) => response.json()).catch((error) => {
      dispatch(pushToast({ message: `${intl.formatMessage(messages.sourceSettingsSelectAccessError)} - ${error}` }));
    });
    setSpinnerVisible(false);
    if (data && data.list && data.list.entries.length) {
      data.list.entries.forEach((item) => {
        if (item.entry.nodeType === 'cm:folder' && item.entry.name === 'documentLibrary') {
          setShowSaveButton(true);
          setDocumentLibraryId(item.entry.id);
        }
      });
    } else {
      dispatch(pushToast({ message: intl.formatMessage(messages.sourceSettingsSelectAccessError) }));
    }
  };

  const resetEditState = () => {
    setShowButtons(false);
    setSpinnerVisible(false);
    setShowSaveButton(false);
  };

  const handleSave = () => {
    const titleToUse = sourceType !== sourceList.NONE ? title : '';
    const documentLibraryIdToUse = sourceType !== sourceList.NONE ? documentLibraryId : '';
    setTitle(titleToUse);
    const newSettings = { sourceType, title: titleToUse, documentLibraryId: documentLibraryIdToUse };
    setOriginalSettings(newSettings);
    saveSettings(index, newSettings);
    setShowEditIcon(sourceType !== sourceList.NONE);
    setReadOnly(sourceType !== sourceList.NONE);
    resetEditState();
  };

  const handleCancel = () => {
    setTitle(originalSettings.title);
    setSourceType(originalSettings.sourceType);
    setDocumentLibraryId(originalSettings.documentLibraryId);
    setShowEditIcon(originalSettings.sourceType !== sourceList.NONE);
    setReadOnly(originalSettings.sourceType !== sourceList.NONE);
    resetEditState();
  };

  const handleStartEdit = () => {
    setReadOnly(false);
    setShowEditIcon(false);
    setShowButtons(true);
  };

  const handleTitleChange = (event) => {
    setShowSaveButton(false);
    setDocumentLibraryId('');
    setTitle(event.target.value);
    debounceFetchSitesFn.current(event.target.value);
  };

  const handleSourceTypeChange = (value) => {
    setShowSaveButton(false);
    if (value !== originalSettings.sourceType) {
      setShowButtons(true);
      if (value === sourceList.NONE) {
        setShowSaveButton(true);
      }
    }
    setSourceType(value);
  };

  const onSelectSourceSite = (ix) => {
    if (autoCompleteData[ix].title !== originalSettings.title) {
      fetchDocumentLibraryID(autoCompleteData[ix].node, isWorkspaceIesV2Enabled);
      setTitle(autoCompleteData[ix].title);
      setAutoCompleteData([]);
    }
  };

  let searchResultRows = [];
  if (autoCompleteData && autoCompleteData.length) {
    const resultItems = autoCompleteData.map((item, ix) =>
      (
        <div key={`id-${item.title}`}>
          <AutoCompleteSuggestionItem onClick={() => onSelectSourceSite(ix)}> {item.title} </AutoCompleteSuggestionItem>
        </div>
      )
    );
    searchResultRows = (
      <List>
        <ListBody>
          {resultItems}
        </ListBody>
      </List>
    );
  }

  return (
    <SectionDetails>
      {(showEditIcon &&
        <EditContainer>
          <RightAlignContainer>
            <Button
              rounded
              iconOnly
              flat
              onClick={() => {
                handleStartEdit();
              }}
            >
              <MaterialIcon id="edit" /></Button>
          </RightAlignContainer>
        </EditContainer>
      )}
      <SourceContainer>
        <Select
          disabled={readOnly}
          label={intl.formatMessage(messages.sourceSettingsSource)}
          value={sourceType}
          onChange={(e) => { handleSourceTypeChange(e.target.value); }}
        >
          <option value={sourceList.NONE}>{intl.formatMessage(messages.sourceSettingsNone)}</option>
          <option value={sourceList.ALFRESCO}>{intl.formatMessage(messages.sourceSettingsAlfresco)}</option>
        </Select>
      </SourceContainer>

      {showTitleInput && (
        <div>
          <Input
            label={intl.formatMessage(messages.sourceSettingsSite)}
            placeholder={intl.formatMessage(messages.sourceSettingsPlaceholder)}
            disabled={readOnly}
            value={title || ''}
            onChange={(e) => { handleTitleChange(e); }}
          />
          {autoCompleteData.length > 0 && (
            <SearchResultsContainer>
              {searchResultRows}
            </SearchResultsContainer>
          )}
        </div>
      )}

      {showButtons && (
        <ActionContainer>
          {showSpinner && (
            <ActionItemContainer>
              <Spinner xsmall color="currentColor" />
            </ActionItemContainer>
          )}
          <ActionItemContainer>
            <Button primary onClick={handleCancel}>{intl.formatMessage(messages.sourceSettingsCancel)}
            </Button>
          </ActionItemContainer>
          {showSaveButton && (
            <ActionItemContainer>
              <Button primary onClick={handleSave}>{intl.formatMessage(messages.sourceSettingsSave)}
              </Button>
            </ActionItemContainer>
          )}
        </ActionContainer>
      )}
    </SectionDetails>
  );
};

SourceSettings.propTypes = {
  index: PropTypes.number,
  settings: PropTypes.object,
  saveSettings: PropTypes.func,
  getFeatureFlagValue: PropTypes.func,
};

const flagNames = [
  featureKeys.WORKSPACE_IES_V2,
];

const mapStateToProps = (state) => ({
  currentUser: selectCurrentMe(state),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect, enhanceWithFeatureFlagSubscription)(SourceSettings, flagNames);
